import { Signal, signal } from '@angular/core';
import {
  IChipSettings,
  IUiIconControlSettings,
  ICheckSettings,
  IDateSettings,
  IDropdownSettings,
  IFileUploadSettings,
  IModalSettings,
  INumberSettings,
  ISectionGroupSettings,
  ISectionSettings,
  ISpreadsheetSettings,
  ITextSettings,
  IUiControlSettings,
  IUiInputSettings,
  IUiLayoutSettings,
  IUiMultiSelectSettings,
  IUiPopupSettings,
  IUiSettings,
  IUiSpreadSheetCell,
  IUiOption,
  ILocationSettings,
  IUiLocationTag,
  IPageSettings,
  IMultiDropdownSettings,
  IRadioGroupSettings,
  IPageLayoutSettings,
  IUiListSettings,
  IUiTreeSettings,
  IRichTextSettings,
} from '../interfaces';
import {
  UI_POPUP_HIDE_STRATEGY,
  TEXT_INPUT_TYPE,
  DATE_SELECTION,
  DROPDOWN_SELECTED_OPTION_STRATEGY,
  FILE_UPLOAD_UI_TYPE,
  SPREADSHEET_ALLOWED_VALUES,
  DateSelectionType,
  IconSizeType,
  TextInputType,
  DropdownSelectedOptionStrategyType,
  FileUploadUiType,
  UiPopupHideStrategyType,
  ICONS,
  IconType,
  ICON_SIZE,
  PAGE_LAYOUT,
} from '../types';

//#region Common

export class SettingsGlobal {
  static settingsUid = 0;
}

export class UiLayoutSettings implements IUiSettings {
  constructor() {
    this.instanceId = SettingsGlobal.settingsUid++;
  }
}

export interface UiLayoutSettings extends IUiLayoutSettings {}

export class UiControlSettings extends UiLayoutSettings implements IUiSettings {
  constructor() {
    super();
  }
}

export interface UiControlSettings extends IUiControlSettings {}

export class UiInputSettings
  extends UiControlSettings
  implements IUiInputSettings
{
  constructor() {
    super();
    this.placeholder = '';
    this.placeholderPersists = false;
    this.disabled = false;
    this.defaultIconSlot = 'right';
    this.labelPosition = 'top';
    this.showClearButton = true;
    this.audited = true;
  }
}

export interface UiInputSettings extends IUiInputSettings {}

export class UiPopupSettings extends UiControlSettings implements IUiSettings {
  static defaultMaxHeight = 250;
  static defaultPopupHideStrategy: UiPopupHideStrategyType =
    UI_POPUP_HIDE_STRATEGY.ClickAnywhere;

  constructor() {
    super();
    this.maxHeight = UiPopupSettings.defaultMaxHeight;
    this.hideStrategy = UiPopupSettings.defaultPopupHideStrategy;
    this.skipOutsideClick = 1;
  }
}

export interface UiPopupSettings extends IUiPopupSettings {}

//#endregion

//#region Ui Directives

export class UiMultiSelectSettings implements IUiSettings {
  constructor() {
    this.listField = 'options';
  }
}

export interface UiMultiSelectSettings extends IUiMultiSelectSettings {}

//#endregion

//#region Input

export class UiIconControlSettings
  extends UiControlSettings
  implements IUiSettings
{
  static defaultIconSize = 'icon-size-[1rem]';
  static defaultIconOnly = false;

  constructor() {
    super();
    this.iconSize = UiIconControlSettings.defaultIconSize;
    this.iconOnly = UiIconControlSettings.defaultIconOnly;
    this.labelPosition = 'embedded';
    this.disabled = false;
  }
}

export interface UiIconControlSettings extends IUiIconControlSettings {}

export class CheckSettings extends UiInputSettings implements IUiSettings {
  static defaultIsToggle = true;

  constructor() {
    super();
    this.isToggle = CheckSettings.defaultIsToggle;
    this.labelPosition = 'right';
  }
}
export interface CheckSettings extends ICheckSettings {}

export class RadioGroupSettings extends UiInputSettings implements IUiSettings {
  constructor() {
    super();
    this.defaultSelect = false;
    this.disabled = false;
    this.optionLabel = DropdownSettings.defaultOptionLabel;
    this.optionValue = DropdownSettings.defaultOptionValue;
    this.labelPosition = 'right';
  }
}

export interface RadioGroupSettings extends IRadioGroupSettings {}

export class DateSettings extends UiInputSettings implements IUiSettings {
  static defaultSelection: DateSelectionType = DATE_SELECTION.Day;
  static defaultDayFormat = 'MM/dd/yyyy';
  static defaultMonthFormat = 'MMM - yyyy';
  static defaultYearFormat = 'yyyy';
  static defaultYearsInPage = 10;
  static defaultPopup: IUiPopupSettings = {
    hideStrategy: UI_POPUP_HIDE_STRATEGY.ClickOutside,
  };

  static defaultFormat(selection?: DateSelectionType): string {
    switch (selection) {
      case DATE_SELECTION.Day:
        return DateSettings.defaultDayFormat;
      case DATE_SELECTION.Month:
        return DateSettings.defaultMonthFormat;
      case DATE_SELECTION.Year:
        return DateSettings.defaultYearFormat;
      default:
        return DateSettings.defaultDayFormat;
    }
  }

  constructor() {
    super();
    this.selection = DateSettings.defaultSelection;
    this.placeholder = DateSettings.defaultFormat(this.selection);
    this.format = DateSettings.defaultFormat(this.selection);
    this.dayButtonLabelFormat = 'MMMM yyyy';
    this.monthButtonLabelFormat = 'yyyy';
    this.yearsInPage = DateSettings.defaultYearsInPage;
    this.autoNavigateToLastSelectedDate = true;
    this.defaultIcon = ICONS.CALENDAR;
    this.popup = new UiPopupSettings();
    this.popup.hideStrategy = DateSettings.defaultPopup.hideStrategy;
    this.popup.skipOutsideClick = 1;
  }
}

export interface DateSettings extends IDateSettings {}

export class MultiDateSettings extends DateSettings implements IDateSettings {
  shrinkCount?: number = 0;
  override placeholder?: string = 'Select';
  constructor() {
    super();
    this.popup!.hideStrategy = UI_POPUP_HIDE_STRATEGY.ClickOutside;
  }
}

export class DropdownSettings
  extends UiInputSettings
  implements IDropdownSettings
{
  //Default Options
  static defaultOptionLabel: keyof IUiOption = 'label';
  static defaultOptionValue: keyof IUiOption = 'value';
  static defaultOptionDeleted: keyof IUiOption = 'isDeleted';
  static defaultOptionsDebounce = 2000;

  static defaultSelectedOptionStrategy: DropdownSelectedOptionStrategyType =
    DROPDOWN_SELECTED_OPTION_STRATEGY.Marked;

  //Default Input
  static defaultValueOutput: 'signal' | 'plain' = 'signal';

  //Default Icons
  static defaultIcon: IconType = ICONS.CHEVRON_DOWN;
  static defaultLoadingIcon: IconType = ICONS.PROGRESS;

  constructor() {
    super();

    //Options
    this.optionLabel = DropdownSettings.defaultOptionLabel;
    this.optionValue = DropdownSettings.defaultOptionValue;

    this.optionsSorting = true;
    this.optionsFiltering = true;
    this.optionsDebounce = DropdownSettings.defaultOptionsDebounce;

    this.selectedOptionStrategy =
      DropdownSettings.defaultSelectedOptionStrategy;

    //Input
    this.storeOnlyValue = true;
    this.showClearButton = true;
    this.allowMultiple = true;
    this.valueOutput = DropdownSettings.defaultValueOutput;

    //Icons
    this.defaultIcon = DropdownSettings.defaultIcon;
    this.loadingIcon = DropdownSettings.defaultLoadingIcon;
    this.hideDefaultIcon = false;

    //Popup
    this.popup = new UiPopupSettings();
    this.popup.hideStrategy = UI_POPUP_HIDE_STRATEGY.ClickOutsideOrOption;
  }
}

export class MultiDropdownSettings
  extends DropdownSettings
  implements IMultiDropdownSettings
{
  shrinkCount = 0;
  override placeholder = 'Select';
  override storeOnlyValue = false;
  constructor() {
    super();
    this.popup!.hideStrategy = UI_POPUP_HIDE_STRATEGY.ClickOutside;
  }
}

export interface DropdownSettings extends IDropdownSettings {}

export class LocationSettings extends DropdownSettings implements IUiSettings {
  static defaultControlSize = 25;
  static defaultValueFormat = 'address1,city,state,country';
  static defaultFieldMap: Array<{
    field: string;
    tags: Array<IUiLocationTag>;
  }> = [
    {
      field: 'address1',
      tags: [
        { tag: 'street_number', writeMode: 'prepend' },
        { tag: 'route', writeMode: 'append' },
        { tag: 'premise', writeMode: 'append' },
      ],
    },
    {
      field: 'zip',
      tags: [{ tag: 'postal_code', writeMode: 'replace' }],
    },
    {
      field: 'city',
      tags: [
        { tag: 'locality', writeMode: 'replace' },
        { tag: 'postal_town', writeMode: 'replace' },
      ],
    },
    {
      field: 'state',
      tags: [{ tag: 'administrative_area_level_1', writeMode: 'replace' }],
    },
    {
      field: 'country',
      tags: [{ tag: 'country', writeMode: 'replace' }],
    },
  ];
  static defaultPopup?: IUiPopupSettings = {
    hideStrategy: UI_POPUP_HIDE_STRATEGY.ClickOutsideOrOption,
  };

  constructor() {
    super();
    this.storeOnlyValue = false;
    this.valueOutput = 'plain';
    this.valueFormat = LocationSettings.defaultValueFormat;
    this.optionLabel = '$formattedValue';
    this.optionValue = '$uid';
    this.optionsDebounce = 0;
    this.optionsFiltering = false;
    this.fieldMap = LocationSettings.defaultFieldMap;
    this.popup = { ...LocationSettings.defaultPopup };
  }
}

export interface LocationSettings extends ILocationSettings {}

export class FileUploadSettings extends UiInputSettings implements IUiSettings {
  static defaultUiType: FileUploadUiType = FILE_UPLOAD_UI_TYPE.Field;
  static defaultAllowMultipleFiles = false;
  static defaultAllowedFileTypes = '.pdf,.xls,.xlsx,.doc,.docx,.txt,.ppt,.pptx';
  static defaultIcon: IconType = ICONS.ATTACH_FILE;
  static showLabel = true;
  static showFooter = true;
  static defaultAutoClearAfterUpload = false;

  constructor() {
    super();
    this.uiType = FileUploadSettings.defaultUiType;
    this.allowMultipleFiles = FileUploadSettings.defaultAllowMultipleFiles;
    this.allowedFileTypes = FileUploadSettings.defaultAllowedFileTypes;
    this.defaultIcon = FileUploadSettings.defaultIcon;
    this.showLabel = FileUploadSettings.showLabel;
    this.showFooter = FileUploadSettings.showFooter;
    this.defaultIcon = FileUploadSettings.defaultIcon;
    this.autoClearAfterUpload = FileUploadSettings.defaultAutoClearAfterUpload;
  }
}

export interface FileUploadSettings extends IFileUploadSettings {}

export class NumberSettings extends UiInputSettings implements IUiSettings {
  static defaultPrefix = '';
  static defaultSuffix = '';
  static defaultOnlyWholeNumbers = false;
  static defaultPermitNegative = true;
  static defaultPermitZero = false;
  static defaultPermitShortcuts = true;
  static defaultAbbr = true;
  static defaultDecimalAmount = 4;
  static defaultThousandsSeparator = ',';

  constructor() {
    super();
    this.prefix = NumberSettings.defaultPrefix;
    this.suffix = NumberSettings.defaultSuffix;
    this.onlyWholeNumbers = NumberSettings.defaultOnlyWholeNumbers;
    this.decimalAmount = NumberSettings.defaultDecimalAmount;
    this.thousandsSeparator = NumberSettings.defaultThousandsSeparator;
    this.permitNegative = NumberSettings.defaultPermitNegative;
    this.permitZero = NumberSettings.defaultPermitZero;
    this.permitShortcuts = NumberSettings.defaultPermitShortcuts;
    this.abbr = NumberSettings.defaultAbbr;
  }
}

export interface NumberSettings extends INumberSettings {}

export class SpreadsheetSettings<T> implements IUiSettings {
  static readonly defaultColumnTitles: Array<string> = [
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
  ];
  static readonly defaultRowTitles: Array<string> = ['1', '2', '3', '4', '5'];
  constructor() {
    this.inputMode = 'directly';

    //Toolbar
    this.showNumberFormat = true;
    this.showCloseToggle = true;

    this.numberFormat = true;

    //Sheet
    this.defaultRowQuantity = SpreadsheetSettings.defaultRowTitles.length;
    this.defaultColumnQuantity = SpreadsheetSettings.defaultColumnTitles.length;
    this.columnTitles = SpreadsheetSettings.defaultColumnTitles;
    this.rowTitles = SpreadsheetSettings.defaultRowTitles;

    //All Cells
    this.allowedValues = SPREADSHEET_ALLOWED_VALUES.All;
  }
}

export interface SpreadsheetSettings<T> extends ISpreadsheetSettings<T> {}

export class UiSpreadSheetCell<T> implements IUiSpreadSheetCell<T> {
  formattedValue: Signal<string | number | undefined> = signal(undefined);
}

export interface UiSpreadSheetCell<T> extends IUiSpreadSheetCell<T> {}

export class TextSettings extends UiInputSettings implements IUiSettings {
  static defaultType: TextInputType = TEXT_INPUT_TYPE.Text;
  static defaultMultiline = false;
  static defaultShowCharCount = false;

  constructor() {
    super();
    this.type = TextSettings.defaultType;
    this.multiline = TextSettings.defaultMultiline;
    this.showCharCount = TextSettings.defaultShowCharCount;
    this.showClearButton = true;
  }
}

export interface TextSettings extends ITextSettings {}

export class RichTextSettings extends UiInputSettings implements IUiSettings {
  constructor() {
    super();
    this.attribution = false;
    this.theme = 'aksia';
    this.heightMin = 150;
    this.heightMax = 500;
    this.placeholderText = 'Edit Your Content Here!';
    this.charCounterCount = false;
    this.toolbarSticky = false;
    this.tabSpaces = 2;
    this.quickInsertEnabled = false;
    this.wordPasteModal = false;
    this.wordPasteKeepFormatting = true;
    // font-family and font-size are intentionally excluded — enforced globally via CSS.
    // This prevents them from ever being stored in the HTML regardless of source
    // (toolbar, paste, programmatic insert).
    this.htmlAllowedStyleProps = [
      'color',
      'background-color',
      'text-align',
      'font-weight',
      'font-style',
      'text-decoration',
      'vertical-align',
    ];
    this.wordAllowedStyleProps = [
      'color',
      'background-color',
      'text-align',
      'font-weight',
      'font-style',
      'text-decoration',
      'vertical-align',
    ];

    this.toolbarButtons = {
      moreText: {
        buttons: [
          'bold',
          'italic',
          'underline',
          'strikeThrough',
          'subscript',
          'superscript',
          'color',
          'backgroundColor',
          'paragraphStyle',
          'clearFormatting',
        ],
        buttonsVisible: 3,
      },
      moreParagraph: {
        buttons: [
          'formatOL',
          'formatUL',
          'paragraphFormat',
          'outdent',
          'indent',
          'quote',
        ],
        align: 'left',
        buttonsVisible: 2,
      },
      moreRich: {
        buttons: [
          'insertLink',
          'insertImage',
          'insertTable',
          'specialCharacters',
          'insertHR',
          'selectAll',
          'fullscreen',
          'html',
        ],
        align: 'left',
        buttonsVisible: 4,
      },
      moreMisc: {
        buttons: ['undo', 'redo', 'help'],
        align: 'right',
      },
    };
  }
}

export interface RichTextSettings extends IRichTextSettings {}

//#endregion

//#region Layout

export class PageSettings extends UiLayoutSettings implements IUiSettings {
  static defaultRowGap = 10;

  constructor() {
    super();
    this.rowGap = PageSettings.defaultRowGap;
  }
}

export interface PageSettings extends IPageSettings {}

export class PageLayoutSettings extends UiControlSettings {
  constructor() {
    super();
    this.type = PAGE_LAYOUT.BLANK;
    this.outlets = {
      TOP: 'top',
      BOTTOM: 'bottom',
      CENTER: undefined,
      LEFT: 'left',
      RIGHT: 'right',
    };
    this.useRouterOutlet = true;
  }
}
export interface PageLayoutSettings extends IPageLayoutSettings {}

export class SectionSettings extends UiControlSettings implements IUiSettings {
  static defaultSectionContentSettings: IUiLayoutSettings = {
    colGap: 5,
    rowGap: 30,
  };

  constructor() {
    super();
    this.isMinimized = false;
    this.isCollapsible = true;
    this.collapsePosition = 'right';
    this.isRemovable = false;
    this.noContentMsg = '';
    this.content = { ...SectionSettings.defaultSectionContentSettings };
  }
}

export interface SectionSettings extends ISectionSettings {}

export class SectionGroupSettings
  extends UiControlSettings
  implements IUiSettings
{
  static defaultSectionGroupContentSettings: IUiLayoutSettings = {
    colGap: 5,
    rowGap: 30,
  };

  constructor() {
    super();
    this.isMinimized = false;
    this.canMinimize = true;
  }
}

export interface SectionGroupSettings extends ISectionGroupSettings {}

export class ModalSettings implements IUiSettings {
  enableCloseOnBackdrop?: boolean = true;
}

export interface ModalSettings extends IModalSettings {}

//#endregion

//#region Presentation

export class ChipSettings implements IUiSettings {}
export interface ChipSettings extends IChipSettings {}

//#endregion

export class ListSettings<T = unknown> implements IUiListSettings<T> {
  reOrdering?: boolean;

  constructor() {
    this.reOrdering = false;
  }
}

export class TreeSettings<T = unknown> implements IUiTreeSettings<T> {}
